
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "../candidate-types";

export const useClearCandidates = () => {
  const [isClearing, setIsClearing] = useState(false);
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);

  const handleClearCandidatesConfirm = async (
    projectId: string | null,
    setCandidates: (candidates: Candidate[]) => void,
    setCandidatesPerRole: React.Dispatch<React.SetStateAction<{ [roleId: string]: Candidate[]; }>>,
    setSelectedRowId: (id: string | null) => void,
    setProcessLog: React.Dispatch<React.SetStateAction<string>>
  ) => {
    if (!projectId) {
      toast.error("Project ID is required");
      return;
    }

    setIsClearing(true);
    setProcessLog("Starting comprehensive candidate data cleanup...");

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("User not authenticated");
        return;
      }

      // First, get all existing candidates to see what we're deleting
      const { data: existingCandidates, error: fetchError } = await supabase
        .from('candidates')
        .select('id, candidate_name, role_title, candidate_id, match_percentage, created_at')
        .eq('project_id', projectId)
        .eq('user_id', user.id);

      if (fetchError) {
        console.error('[ClearCandidates] Error fetching existing candidates:', fetchError);
        toast.error("Failed to fetch existing candidates");
        return;
      }

      if (existingCandidates && existingCandidates.length > 0) {
        console.log(`[ClearCandidates] Found ${existingCandidates.length} candidates to clear:`, 
          existingCandidates.map(c => ({
            id: c.id,
            name: c.candidate_name,
            role: c.role_title,
            candidate_id: c.candidate_id,
            created_at: c.created_at
          }))
        );

        setProcessLog(prev => `${prev}\nFound ${existingCandidates.length} candidates to clear. Proceeding with deletion...`);

        // Group by role for better logging
        const candidatesByRole = existingCandidates.reduce((acc, candidate) => {
          const role = candidate.role_title;
          if (!acc[role]) acc[role] = [];
          acc[role].push(candidate);
          return acc;
        }, {} as Record<string, typeof existingCandidates>);

        Object.keys(candidatesByRole).forEach(role => {
          const roleCandidates = candidatesByRole[role];
          setProcessLog(prev => `${prev}\nRole "${role}": ${roleCandidates.length} candidates`);
        });

        // Delete candidates - use a more thorough approach
        const { error: candidatesError } = await supabase
          .from('candidates')
          .delete()
          .eq('project_id', projectId)
          .eq('user_id', user.id);

        if (candidatesError) {
          console.error('[ClearCandidates] Error clearing candidates:', candidatesError);
          toast.error("Failed to clear candidates");
          setProcessLog(prev => `${prev}\nError clearing candidates: ${candidatesError.message}`);
          return;
        }

        // Verify deletion was successful with multiple attempts
        let verificationAttempts = 0;
        let remainingCandidates;
        do {
          const { data: verifyData, error: verifyError } = await supabase
            .from('candidates')
            .select('id, candidate_name, role_title')
            .eq('project_id', projectId)
            .eq('user_id', user.id);

          if (verifyError) {
            console.error('[ClearCandidates] Error verifying candidate deletion:', verifyError);
            setProcessLog(prev => `${prev}\nWarning: Could not verify complete deletion`);
            break;
          }

          remainingCandidates = verifyData;
          verificationAttempts++;

          if (remainingCandidates && remainingCandidates.length > 0 && verificationAttempts < 3) {
            console.log(`[ClearCandidates] Verification attempt ${verificationAttempts}: ${remainingCandidates.length} candidates still remain, retrying deletion...`);
            
            // Try to delete remaining candidates again
            await supabase
              .from('candidates')
              .delete()
              .eq('project_id', projectId)
              .eq('user_id', user.id);
              
            // Wait a moment before next verification
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } while (remainingCandidates && remainingCandidates.length > 0 && verificationAttempts < 3);

        if (remainingCandidates && remainingCandidates.length > 0) {
          console.error('[ClearCandidates] Warning: Some candidates were not deleted after multiple attempts:', remainingCandidates);
          setProcessLog(prev => `${prev}\nWarning: ${remainingCandidates.length} candidates still remain after deletion attempts`);
        } else {
          console.log('[ClearCandidates] Verified: All candidates successfully deleted');
          setProcessLog(prev => `${prev}\nVerified: All ${existingCandidates.length} candidates successfully deleted`);
        }
      } else {
        console.log('[ClearCandidates] No candidates found to clear');
        setProcessLog(prev => `${prev}\nNo candidates found to clear`);
      }

      // Update role_jd table with enhanced verification
      setProcessLog(prev => `${prev}\nUpdating role assignments...`);

      const { data: roles, error: rolesError } = await supabase
        .from('role_jd')
        .select('id, role_title')
        .eq('project_id', projectId)
        .eq('user_id', user.id);

      if (rolesError) {
        console.error('[ClearCandidates] Error fetching roles for update:', rolesError);
        setProcessLog(prev => `${prev}\nWarning: Could not fetch roles for cleanup`);
      } else {
        const { error: roleJdError } = await supabase
          .from('role_jd')
          .update({
            candidate_id_1: null,
            candidate_id_2: null,
            candidate_id_3: null,
            number_of_candidates: 0,
            filled: false
          })
          .eq('project_id', projectId)
          .eq('user_id', user.id);

        if (roleJdError) {
          console.error('[ClearCandidates] Error updating role_jd:', roleJdError);
          toast.error("Failed to update role assignments");
          setProcessLog(prev => `${prev}\nError updating role assignments: ${roleJdError.message}`);
          return;
        } else {
          console.log(`[ClearCandidates] Successfully reset ${roles?.length || 0} role assignments`);
          setProcessLog(prev => `${prev}\nSuccessfully reset ${roles?.length || 0} role assignments`);
        }
      }

      // Clear state immediately and prevent refetch
      console.log('[ClearCandidates] Clearing local state...');
      setCandidates([]);
      
      // Clear all candidates for all roles to ensure UI reflects the cleared state
      if (roles) {
        const emptyCandidatesPerRole: { [roleId: string]: Candidate[]; } = {};
        roles.forEach(role => {
          emptyCandidatesPerRole[role.id] = [];
        });
        setCandidatesPerRole(emptyCandidatesPerRole);
        console.log('[ClearCandidates] Cleared candidates for all roles:', Object.keys(emptyCandidatesPerRole));
      }
      
      setSelectedRowId(null);
      
      setProcessLog(prev => `${prev}\n\nCandidate data cleanup completed successfully. System is ready for new candidate searches.`);
      toast.success("All candidates cleared successfully");

    } catch (error) {
      console.error('[ClearCandidates] Error in handleClearCandidatesConfirm:', error);
      toast.error("Failed to clear candidates");
      setProcessLog("Error clearing candidates. Please try again.");
    } finally {
      setIsClearing(false);
      setShowClearConfirmDialog(false);
    }
  };

  const handleClearCandidates = () => {
    setShowClearConfirmDialog(true);
  };

  return {
    isClearing,
    showClearConfirmDialog,
    setShowClearConfirmDialog,
    handleClearCandidatesConfirm,
    handleClearCandidates
  };
};
